import { Link, router } from "@inertiajs/react"
import { ArrowUpRight, PlayCircle } from "lucide-react"
import type { MouseEvent } from "react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { cn } from "@/lib/utils"
import { adminReplayIndexPath, adminReplayPath, chatPath } from "@/routes"

export interface ReplaySessionSummary {
  id: number
  external_id: string
  session_token: string
  stage: string
  created_at: string
  event_count: number
  message_count: number
  duration_ms: number
}

export interface ReplayListing {
  conversations: ReplaySessionSummary[]
  total_count: number
  shown_filtered: number
}

function formatDuration(ms: number): string {
  if (!ms || ms < 1000) return "—"
  const totalSeconds = Math.round(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return minutes > 0 ? `${minutes}m ${seconds}s` : `${seconds}s`
}

function resumeHref(id: number): string {
  return `${chatPath()}?conversation_id=${id}`
}

export function ReplaySessionsCard({
  replay,
  className,
}: {
  replay: ReplayListing
  className?: string
}) {
  const { conversations, total_count } = replay

  // The whole row is the primary action (open the replay). Honour ⌘/Ctrl-click to
  // open in a new tab; clicks on a real link/button inside the row are left alone
  // so "Resume" opens the chat rather than the replay view.
  const openReplay = (event: MouseEvent, id: number) => {
    if ((event.target as HTMLElement).closest("a, button")) return
    const href = adminReplayPath(id)
    if (event.metaKey || event.ctrlKey) {
      window.open(href, "_blank")
    } else {
      router.visit(href)
    }
  }

  return (
    <Card className={cn("flex min-h-[420px] flex-col gap-0 py-0", className)}>
      <CardHeader className="flex flex-row items-center justify-between gap-2 border-b py-4">
        <div>
          <CardTitle>Recent conversations</CardTitle>
          <p className="text-muted-foreground mt-1 text-sm">
            Click a row to replay its full event history.
          </p>
        </div>
        <Link
          href={adminReplayIndexPath()}
          className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1 text-sm font-medium whitespace-nowrap"
        >
          Open replay library
          <ArrowUpRight className="size-4" />
        </Link>
      </CardHeader>

      <CardContent className="min-h-0 flex-1 overflow-auto p-0">
        {conversations.length === 0 ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 py-16 text-center">
            <p className="text-muted-foreground text-sm">
              No conversations yet.
            </p>
            <Button asChild>
              <a href={`${chatPath()}?script=demo_golden_path`}>
                <PlayCircle className="size-4" />
                Run live demo
              </a>
            </Button>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-muted-foreground bg-muted/40 sticky top-0 z-10 text-xs uppercase">
              <tr>
                <th className="px-6 py-3 text-left font-medium">
                  Conversation
                </th>
                <th className="px-6 py-3 text-left font-medium">Stage</th>
                <th className="px-6 py-3 text-right font-medium">Events</th>
                <th className="px-6 py-3 text-right font-medium">Messages</th>
                <th className="px-6 py-3 text-right font-medium">Duration</th>
                <th className="px-6 py-3 text-left font-medium">Created</th>
                <th className="px-6 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-border divide-y">
              {conversations.map((c) => (
                <tr
                  key={c.id}
                  onClick={(event) => openReplay(event, c.id)}
                  className="hover:bg-muted/50 cursor-pointer transition-colors"
                >
                  <td className="text-foreground px-6 py-3 font-medium whitespace-nowrap">
                    {c.external_id}
                  </td>
                  <td className="px-6 py-3 whitespace-nowrap">
                    <Badge variant="secondary" className="capitalize">
                      {c.stage.replace(/_/g, " ")}
                    </Badge>
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap tabular-nums">
                    {c.event_count}
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap tabular-nums">
                    {c.message_count}
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap tabular-nums">
                    {formatDuration(c.duration_ms)}
                  </td>
                  <td className="text-muted-foreground px-6 py-3 whitespace-nowrap">
                    {new Date(c.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-3 text-right whitespace-nowrap">
                    <a
                      href={resumeHref(c.id)}
                      title="Open in chat and pick up where it left off"
                      className="text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                      onClick={(event) => event.stopPropagation()}
                    >
                      Resume
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent>

      {conversations.length > 0 && (
        <CardFooter className="text-muted-foreground border-t py-3 text-xs">
          Showing {conversations.length} of {total_count} conversations
        </CardFooter>
      )}
    </Card>
  )
}
